<script>
import Vue from 'vue'
import {
  mapActions
} from 'vuex'

export default {
  data() {
    return {
      axis_x: null,
      axis_x2: null,
      axis_y: null,
      axis_y2: null,
      content_bounds: {
        x: 0,
        y: 0,
        height: 0,
        width: 0
      },
      first_update: true,
      height: 0,
      width: 0
    }
  },
  methods: {
    ...mapActions([
      'update_component_frame'
    ]),
    async get_axis_x() {
      return new Promise(resolve => {
        Vue.nextTick(() => {
          resolve(this.$slots.axis_x ?
            this.$slots.axis_x[0].componentInstance : null)
        })
      })
    },
    async get_axis_y() {
      return new Promise(resolve => {
        Vue.nextTick(() => {
          resolve(this.$slots.axis_y ?
            this.$slots.axis_y[0].componentInstance : null)
        })
      })
    },
    async get_axis_x2() {
      return new Promise(resolve => {
        Vue.nextTick(() => {
          resolve(this.$slots.axis_x2 ?
            this.$slots.axis_x2[0].componentInstance : null)
        })
      })
    },
    async get_axis_y2() {
      return new Promise(resolve => {
        Vue.nextTick(() => {
          resolve(this.$slots.axis_y2 ?
            this.$slots.axis_y2[0].componentInstance : null)
        })
      })
    },
    get_content_bounds() { return this.content_bounds },
    async update() {
      return new Promise(resolve => {
        setTimeout(async () => {
          const container = this.$refs.container
          if(!container) return

          await Promise.all([
            this.get_axis_x(),
            this.get_axis_y(),
            this.get_axis_x2(),
            this.get_axis_y2()
          ]).then(result => {
            this.axis_x = result[0]
            this.axis_y = result[1]
            this.axis_x2 = result[2]
            this.axis_y2 = result[3]
          }).catch(e => {
            // TODO: Emit an error event
          })

          this.height = container.clientHeight
          this.width = container.clientWidth

          // Compute the content bounds based on the sizes of each axis
          let content_bounds = {
            x: 0,
            y: 0,
            height: this.height,
            width: this.width
          }

          // Adjust the content bounds based on the size of the axes
          if(this.axis_x) {
            await this.axis_x.update()
            content_bounds.height -= this.axis_x.get_size()
          }

          if(this.axis_y) {
            await this.axis_y.update()
            content_bounds.width -= this.axis_y.get_size()
            content_bounds.x = this.axis_y.get_size()
          }

          if(this.axis_x2) {
            await this.axis_x2.update()
            content_bounds.height -= this.axis_x2.get_size()
            content_bounds.y = this.axis_x2.get_size()
          }

          if(this.axis_y2) {
            await this.axis_y2.update()
            content_bounds.width -= this.axis_y2.get_size()
          }

          // Math.floor the content bounds
          this.content_bounds = {
            x: Math.floor(content_bounds.x),
            y: Math.floor(content_bounds.y),
            width: Math.max(Math.floor(content_bounds.width), 0),
            height: Math.max(Math.floor(content_bounds.height), 0)
          }

          // Layout the axes based on the content bounds
          if(this.axis_x) {
            this.axis_x.x = content_bounds.x
            this.axis_x.y = content_bounds.height
            this.axis_x.y += this.axis_x2 ? this.axis_x2.get_size() : 0
            this.axis_x.range = [0, content_bounds.width]
          }

          if(this.axis_y) {
            this.axis_y.y = this.axis_x2 ? this.axis_x2.get_size() : 0
            this.axis_y.range = [content_bounds.height, 0]
          }

          if(this.axis_x2) {
            this.axis_x2.x = content_bounds.x
            this.axis_x2.range = [0, content_bounds.width]
          }

          if(this.axis_y2) {
            this.axis_y2.x = content_bounds.x + content_bounds.width
            this.axis_y2.y = this.axis_x2 ? this.axis_x2.get_size() : 0
            this.axis_y2.range = [content_bounds.height, 0]
          }

          this.first_update = false
          resolve()
        }, this.first_update ? 200 : 0)
      })
    }
  },
  mounted() {
    this.update_component_frame(this.id)
  },
  props: {
    bound_rect_visible: {type: Boolean, default: () => true},
    clip_content: {type: Boolean, default: () => true},
    frame: {type: Number, default: () => 0},
    id: {type: String, default: () => 'untitled'},
    min_width: {type: Number, default: () => 128},
    min_height: {type: Number, default: () => 128}
  },
  watch: {
    async frame() {
      await this.update()
      this.$emit('update', {
        x: this.axis_x,
        x2: this.axis_x2,
        y: this.axis_y,
        y2: this.axis_y2
      })
    }
  }
}
</script>
<template>
<div :id='`${id}-plot`'
  class='candela-plot d-flex flex-column flex-grow-1 flex-shrink-0 justify-stretch align-stretch ma-2'>
  <div :id='`${id}-svg-container`' ref='container'
    class='d-flex flex-grow-1 flex-nowrap'
    :style='{minWidth: `${min_width}px`, minHeight: `${min_height}px`}'>
    <svg :id='`${id}-svg`' ref='svg' :width='width' :height='height'>
      <defs>
        <clipPath :id='`content-clip-${id}`'>
          <rect x='1' y='1'
            :width='Math.max(content_bounds.width-1, 0)'
            :height='Math.max(content_bounds.height-1, 0)'/>
        </clipPath>
        <slot name='def-1'/>
        <slot name='def-2'/>
        <slot name='def-3'/>
      </defs>
      <g transform='translate(0.5, 0.5)'>
        <g :transform='`translate(${content_bounds.x} ${content_bounds.y})`'>
          <slot name='background'/>
        </g>
        <slot name='axis_x'></slot>
        <slot name='axis_y'></slot>
        <slot name='axis_x2'></slot>
        <slot name='axis_y2'></slot>
        <g :transform='`translate(${content_bounds.x} ${content_bounds.y})`'
          :clip-path='clip_content ? `url(#content-clip-${id})` : ``'>
          <slot name='content'/>
        </g>
        <rect v-if='bound_rect_visible' class='bounds-rect'
          :transform='`translate(${content_bounds.x} ${content_bounds.y})`'
          :width='Math.max(content_bounds.width, 0)'
          :height='Math.max(content_bounds.height + 1, 0)' fill='none'/>
      </g>
    </svg>
    <div class='html-content ml-4'
      :style='{
        width: `${content_bounds.width}px`,
        height: `${content_bounds.height}px`,
        left: `${content_bounds.x}px`}'>
      <slot name='html-content'/>
    </div>
  </div>
  <div :id='`${id}-bottom-content`' class='flex-shrink-1' :style='{
    marginLeft: content_bounds.x + "px",
    marginRight: (width - content_bounds.width - content_bounds.x) + "px"}'>
    <slot name='bottom-content'/>
  </div>
</div>
</template>

<style lang='sass' scoped>
svg
  overflow: visible
  position: absolute

.candela-plot
  max-width: 100%
  .html-content
    position: absolute

.theme--dark
  .bounds-rect
    stroke: var(--v-grey-darken7)

.theme--light
  .bounds-rect
    stroke: var(--v-grey-darken2)
</style>
